import React, { useState, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import apiClient from '../services/api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

/**
 * StationDetail.jsx
 * Drilldown station-level detail view
 * Props: lang, theme, module (object), station (object), onBack, onNavigateStation
 */

const fmtAmount = (v) => {
  if (v === undefined || v === null) return '₭0';
  const num = Number(v);
  if (isNaN(num)) return '₭0';
  if (num >= 1e9) {
    return `₭${parseFloat((num / 1e9).toFixed(2))}B`;
  } else if (num >= 1e6) {
    return `₭${parseFloat((num / 1e6).toFixed(2))}M`;
  } else {
    return `₭${num.toLocaleString()}`;
  }
};

const StationDetail = ({ lang = 'lo', theme = 'dark', module, station, onBack, onNavigateStation, dateRange, customStart, customEnd }) => {
  const [txPage, setTxPage] = useState(0);
  const [stations, setStations] = useState([]);
  const [moduleKpi, setModuleKpi] = useState(null);
  const [loadingStations, setLoadingStations] = useState(true);
  const [selectedStationIds, setSelectedStationIds] = useState([]);
  const [stationPage, setStationPage] = useState(1);
  const TX_PER_PAGE = 15;
  const STATIONS_PER_PAGE = 10;

  const i18n = {
    lo: {
      back: '← ກັບຄືນ',
      stationDetail: 'ລາຍລະອຽດດ່ານ',
      revenue: 'ລາຍຮັບ',
      transactions: 'ທຸລະກຳ',
      avg: 'ສະເລ່ຍ/ທຸລະກຳ',
      status: 'ສະຖານະ',
      recentTx: 'ທຸລະກຳຫຼ້າສຸດ',
      plate: 'ປ້າຍທະບຽນ',
      type: 'ປະເພດ',
      amount: 'ຈຳນວນ (₭)',
      time: 'ເວລາ',
      ok: 'ສຳເລັດ',
      pen: 'ລໍຖ້າ',
      fail: 'ລົ້ມເຫຼວ',
      hourlyTitle: 'ປະລິມານຕໍ່ຊົ່ວໂມງ',
      dailyTitle: 'ລາຍຮັບ 7 ວັນ',
      allStations: 'ທຸກດ່ານ',
      prev: '← ກ່ອນ',
      next: 'ຕໍ່ໄປ →',
      totalRevenue: 'ລາຍຮັບລວມ',
      topStation: 'ດ່ານຍອດນິຍົມ',
      loading: 'ກຳລັງໂຫຼດ...',
      share: 'ສ່ວນແບ່ງ',
      code: 'ລະຫັດ',
      station: 'ດ່ານ',
      noSelection: 'ກະລຸນາເລືອກຢ່າງໜ້ອຍໜຶ່ງດ່ານເພື່ອປຽບທຽບ',
      vsYesterday: 'ທຽບກັບມື້ວານ',
      dayOnChange: 'ທຽບມື້ກ່ອນໜ້າ',
      growthTitle: 'ການເຕີບໂຕລາຍວັນ (%)',
      formula: 'ອັດຕາການເຕີບໂຕ (%) = ((ລາຍຮັບມື້ນີ້ - ລາຍຮັບມື້ກ່ອນ) / ລາຍຮັບມື້ກ່ອນ) × 100',
    },
    th: {
      back: '← กลับ',
      stationDetail: 'รายละเอียดด่าน',
      revenue: 'รายได้',
      transactions: 'ธุรกรรม',
      avg: 'เฉลี่ย/ธุรกรรม',
      status: 'สถานะ',
      recentTx: 'ธุรกรรมล่าสุด',
      plate: 'ป้ายทะเบียน',
      type: 'ประเภท',
      amount: 'จำนวน (₭)',
      time: 'เวลา',
      ok: 'สำเร็จ',
      pen: 'รอดำเนินการ',
      fail: 'ล้มเหลว',
      hourlyTitle: 'ปริมาณต่อชั่วโมง',
      dailyTitle: 'รายได้ 7 วัน',
      allStations: 'ทุกด่าน',
      prev: '← ก่อน',
      next: 'ถัดไป →',
      totalRevenue: 'รายได้รวม',
      topStation: 'ด่านยอดนิยม',
      loading: 'กำลังโหลด...',
      share: 'สัดส่วน',
      code: 'รหัส',
      station: 'ด่าน',
      noSelection: 'กรุณาเลือกอย่างน้อยหนึ่งด่านเพื่อเปรียบเทียบ',
      vsYesterday: 'เทียบกับเมื่อวาน',
      dayOnChange: 'เทียบวันก่อนหน้า',
      growthTitle: 'การเติบโตรายวัน (%)',
      formula: 'อัตราการเติบโต (%) = ((รายได้วันนี้ - รายได้วันก่อนหน้า) / รายได้วันก่อนหน้า) × 100',
    },
    en: {
      back: '← Back',
      stationDetail: 'Station Detail',
      revenue: 'Revenue',
      transactions: 'Transactions',
      avg: 'Avg / Txn',
      status: 'Status',
      recentTx: 'Recent Transactions',
      plate: 'License Plate',
      type: 'Type',
      amount: 'Amount (₭)',
      time: 'Time',
      ok: 'Success',
      pen: 'Pending',
      fail: 'Failed',
      hourlyTitle: 'Hourly Volume',
      dailyTitle: '7-Day Revenue',
      allStations: 'All Stations',
      prev: '← Prev',
      next: 'Next →',
      totalRevenue: 'Total Revenue',
      topStation: 'Top Station',
      loading: 'Loading...',
      share: 'Share',
      code: 'Code',
      station: 'Station',
      noSelection: 'Please select at least one station to compare',
      vsYesterday: 'vs yesterday',
      dayOnChange: 'vs prev day',
      growthTitle: 'Daily Growth (%)',
      formula: 'Growth (%) = ((Today\'s Revenue - Prev Day\'s Revenue) / Prev Day\'s Revenue) × 100',
    },
  };

  const t = (key) => i18n[lang]?.[key] || i18n.en[key] || key;

  // Fetch stations from API
  useEffect(() => {
    if (!module?.key) return;
    setLoadingStations(true);
    setStationPage(1);
    (async () => {
      try {
        // Build filter parameters
        const params = {};
        if (dateRange === 'customRange') {
          if (customStart && customEnd) {
            params.startDate = customStart;
            params.endDate = customEnd;
          }
        } else {
          const filterDaysMap = { filterToday: '1', filter7: '7', filter30: '30', filter90: '90', allSources: 'all' };
          const days = filterDaysMap[dateRange];
          if (days) params.days = days;
        }

        const [stationsRes, kpiRes] = await Promise.all([
          apiClient.modulesGetStations(module.key, params),
          apiClient.dashboardGetKpi(params),
        ]);
        if (stationsRes?.success && Array.isArray(stationsRes.data)) {
          // Calculate percentages
          const totalAmt = stationsRes.data.reduce((s, r) => s + (r.amount || 0), 0);
          const withPct = stationsRes.data.map(s => ({
            ...s,
            pct: totalAmt > 0 ? Math.round((s.amount / totalAmt) * 100) : 0,
            amountFormatted: fmtAmount(s.amount),
          }));
          setStations(withPct);
          setSelectedStationIds(withPct.map(s => s.id));
        }
        if (kpiRes?.success && kpiRes.data?.[module.key]) {
          setModuleKpi(kpiRes.data[module.key]);
        }
      } catch (e) {
        console.warn('StationDetail fetch:', e.message);
      }
      setLoadingStations(false);
    })();
  }, [module?.key, dateRange, customStart, customEnd]);

  // ── Fetch real transactions + trend from API ──────────────────────────────
  const [txList, setTxList] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);

  useEffect(() => {
    if (!station || !module?.key) return;
    setLoadingTx(true);
    (async () => {
      try {
        const res = await apiClient.modulesGetStationDetail(module.key, station.id);
        if (res?.success) {
          // Transactions
          const txns = (res.transactions || []).map(tx => ({
            id: tx.id,
            type: tx.vehicleType || '-',
            plate: tx.plate || '-',
            amount: tx.amount || 0,
            status: (tx.status || 'ok').toLowerCase().replace('success', 'ok').replace('cancelled', 'fail').replace('completed', 'ok'),
            time: tx.recordedTime ? new Date(tx.recordedTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-',
            method: tx.method,
            paymentType: tx.paymentType,
            operator: tx.operator,
          }));
          setTxList(txns);

          // Daily trend (7-day)
          if (res.trend && res.trend.length > 0) {
            setDailyTrend(res.trend);
          }
        }
      } catch (e) {
        console.warn('StationDetail tx fetch:', e.message);
      }
      setLoadingTx(false);
    })();
  }, [station?.id, module?.key]);

  const pagedTx = txList.slice(txPage * TX_PER_PAGE, (txPage + 1) * TX_PER_PAGE);
  const totalPages = Math.ceil(txList.length / TX_PER_PAGE);

  // 7-day daily growth calculations
  const dailyChangesChart = (() => {
    if (dailyTrend.length === 0) return { labels: [], data: [] };
    const sliceCount = 7;
    const startIndex = Math.max(0, dailyTrend.length - sliceCount);
    const labels = [];
    const data = [];
    for (let i = startIndex; i < dailyTrend.length; i++) {
      const current = dailyTrend[i];
      let pct = 0;
      if (i > 0) {
        const prev = dailyTrend[i - 1];
        if (prev.amount > 0) {
          pct = ((current.amount - prev.amount) / prev.amount) * 100;
        }
      }
      const dt = new Date(current.date + 'T00:00:00');
      labels.push(dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));
      data.push(pct);
    }
    return { labels, data };
  })();

  // 7-day revenue from API trend
  const sevenDayLabels = dailyTrend.length > 0
    ? dailyTrend.slice(-7).map(d => {
        const dt = new Date(d.date + 'T00:00:00');
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      })
    : Array.from({ length: 7 }, (_, i) => {
        const d = new Date(); d.setDate(d.getDate() - (6 - i));
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      });
  const sevenDayRevenue = dailyTrend.length > 0
    ? dailyTrend.slice(-7).map(d => +((d.amount || 0) * 1000).toFixed(2))
    : Array.from({ length: 7 }, () => 0);

  const chartTextColor = theme === 'light' ? '#546e7a' : '#8892a4';
  const gridColor = 'rgba(255,255,255,0.06)';
  const tooltipStyle = { backgroundColor: 'rgba(255,255,255,0.96)', titleColor: '#2c3e50', bodyColor: '#546e7a', borderColor: '#e1e4e8', borderWidth: 1, padding: 12 };

  const growthChartData = {
    labels: dailyChangesChart.labels,
    datasets: [{
      label: 'Growth',
      data: dailyChangesChart.data,
      backgroundColor: dailyChangesChart.data.map(v => v >= 0 ? 'rgba(82, 196, 26, 0.22)' : 'rgba(245, 34, 45, 0.22)'),
      borderColor: dailyChangesChart.data.map(v => v >= 0 ? '#52c41a' : '#f5222d'),
      borderWidth: 1.5,
      borderRadius: 4,
    }],
  };

  const growthChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (v) => `${v.raw >= 0 ? '+' : ''}${v.raw.toFixed(2)}%`
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: chartTextColor, font: { size: 9 } } },
      y: { 
        grid: { color: gridColor }, 
        ticks: { 
          color: chartTextColor, 
          font: { size: 9 },
          callback: (v) => `${v >= 0 ? '+' : ''}${v}%`
        } 
      },
    },
  };

  const barLabelsPlugin = {
    id: 'barLabels',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      ctx.save();
      ctx.font = 'bold 9px var(--font)';
      ctx.fillStyle = theme === 'dark' ? '#8892a4' : '#546e7a';
      ctx.textAlign = 'center';
      chart.getDatasetMeta(0).data.forEach((bar, index) => {
        const value = data.datasets[0].data[index];
        if (value === null || value === undefined) return;
        const label = `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
        const isPositive = value >= 0;
        ctx.textBaseline = isPositive ? 'bottom' : 'top';
        ctx.fillText(label, bar.x, bar.y + (isPositive ? -4 : 4));
      });
      ctx.restore();
    }
  };

  const dailyChartData = {
    labels: sevenDayLabels,
    datasets: [{ label: '₭ Million', data: sevenDayRevenue, borderColor: module?.color || '#1890ff', backgroundColor: `${module?.color || '#1890ff'}15`, fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: module?.color || '#1890ff' }],
  };

  const miniOptions = (yLabel) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { grid: { display: false }, ticks: { color: chartTextColor, font: { size: 9 } } },
      y: { grid: { color: gridColor }, ticks: { color: chartTextColor, font: { size: 10 }, callback: yLabel } },
    },
  });

  const statusColors = {
    ok:   { bg: 'rgba(82,196,26,.15)',  color: '#52c41a', label: t('ok') },
    pen:  { bg: 'rgba(250,173,20,.15)', color: '#faad14', label: t('pen') },
    fail: { bg: 'rgba(245,34,45,.15)',  color: '#f5222d', label: t('fail') },
  };

  const moduleColor = module?.color || '#1890ff';
  const moduleName = module?.name?.[lang] || module?.name?.en || module?.key || '';



  const handleToggleStationSelection = (id) => {
    setSelectedStationIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };

  const handleToggleSelectAll = () => {
    setSelectedStationIds(selectedStationIds.length === stations.length ? [] : stations.map(s => s.id));
  };

  const PALETTE = ['#1890ff', '#52c41a', '#fa8c16', '#f5222d', '#9254de', '#13c2c2', '#eb2f96', '#faad14'];
  const comparedStations = stations.filter(s => selectedStationIds.includes(s.id));
  const comparedTotalAmt = comparedStations.reduce((s, r) => s + (r.amount || 0), 0);
  const comparedStationsWithRelativePct = comparedStations.map(s => ({
    ...s,
    relativePct: comparedTotalAmt > 0 ? Math.round((s.amount / comparedTotalAmt) * 100) : 0
  }));

  if (!station) {
    // Station list view (module overview)
    return (
      <div className="main">
        <div className="section-header">
          <div>
            <div className="section-title" style={{ color: moduleColor }}>{moduleName}</div>
            <div className="section-sub">{t('allStations')}</div>
          </div>
        </div>

        {/* Module KPIs from API */}
        <div className="kpi-grid">
          {[
            { label: t('totalRevenue'), val: moduleKpi ? fmtAmount(moduleKpi.total) : '...', color: moduleColor },
            { label: t('transactions'), val: moduleKpi ? moduleKpi.transactions.toLocaleString() : '...', color: '#1890ff' },
            { label: t('avg'), val: moduleKpi ? fmtAmount(moduleKpi.average) : '...', color: '#9254de' },
            { label: t('topStation'), val: moduleKpi?.topStation || 'N/A', color: '#52c41a' },
          ].map((k, i) => (
            <div key={i} className="kpi">
              <div className="kpi-accent" style={{ background: k.color }} />
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value" style={{ color: k.color, fontSize: i === 3 ? '16px' : undefined }}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Station Comparison Charts */}
        {!loadingStations && stations.length > 0 && (
          comparedStations.length === 0 ? (
            <div className="card" style={{ marginBottom: '24px', padding: '40px', textAlign: 'center', color: 'var(--text3)', border: '1px dashed var(--border)', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{t('noSelection')}</div>
            </div>
          ) : (
            <div className="chart-row" style={{ marginBottom: '24px' }}>
              <div className="card">
                <div className="card-title">
                  {lang === 'lo' ? 'ລາຍຮັບແຍກຕາມດ່ານ' : lang === 'th' ? 'รายได้แยกตามด่าน' : 'Revenue by Station'}
                </div>
                <div className="card-sub" style={{ fontSize: '11px', color: 'var(--text3)' }}>
                  {lang === 'lo' ? 'ລ້ານ ກີບ' : lang === 'th' ? 'ล้าน กีบ' : '₭ Million'}
                </div>
                <div className="chart-wrap" style={{ height: '200px', marginTop: '12px' }}>
                  <Bar 
                    data={{
                      labels: comparedStations.map(s => lang === 'lo' ? s.name_la : s.name_en),
                      datasets: [{
                        data: comparedStations.map(s => s.amount / 1000000),
                        backgroundColor: comparedStations.map(s => PALETTE[stations.findIndex(st => st.id === s.id) % PALETTE.length]),
                        borderRadius: 4,
                      }]
                    }} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false }, tooltip: tooltipStyle },
                      scales: {
                        x: { grid: { display: false }, ticks: { color: chartTextColor, font: { size: 9 } } },
                        y: { grid: { color: gridColor }, ticks: { color: chartTextColor, font: { size: 10 }, callback: v => `₭${v.toLocaleString()}M` } },
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="card">
                <div className="card-title">
                  {lang === 'lo' ? 'ສ່ວນແບ່ງລາຍຮັບ' : lang === 'th' ? 'สัดส่วนรายได้' : 'Share'}
                </div>
                <div className="card-sub" style={{ fontSize: '11px', color: 'var(--text3)' }}>
                  {lang === 'lo' ? '% ຂອງທັງໝົດ' : lang === 'th' ? '% ของทั้งหมด' : '% of total'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'center', marginTop: '12px' }}>
                  <div className="chart-wrap" style={{ height: '160px' }}>
                    <Doughnut 
                      data={{
                        labels: comparedStations.map(s => lang === 'lo' ? s.name_la : s.name_en),
                        datasets: [{
                          data: comparedStationsWithRelativePct.map(s => s.relativePct),
                          backgroundColor: comparedStations.map(s => PALETTE[stations.findIndex(st => st.id === s.id) % PALETTE.length]),
                          borderWidth: 0,
                        }]
                      }} 
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '70%',
                        plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: v => `  ${v.label}: ${v.raw}%` } } }
                      }} 
                    />
                  </div>
                  <div className="mod-list" style={{ maxHeight: '160px', overflowY: 'auto', paddingRight: '4px' }}>
                    {comparedStationsWithRelativePct.map((s) => {
                      const originalIdx = stations.findIndex(st => st.id === s.id);
                      const color = PALETTE[originalIdx % PALETTE.length];
                      return (
                        <div key={s.id} className="mod-item" onClick={() => onNavigateStation(s.id)} style={{ cursor: 'pointer' }}>
                          <div className="mod-dot" style={{ background: color }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="mod-name" style={{ fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {lang === 'lo' ? s.name_la : s.name_en}
                            </div>
                            <div style={{ fontSize: '9px', color: 'var(--text3)' }}>
                              {s.amountFormatted}
                            </div>
                          </div>
                          <div className="mod-val" style={{ fontSize: '11px', fontWeight: '700', color: color }}>
                            {s.relativePct}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* Station list */}
        <div className="card">
          <div className="card-title">{moduleName} — Stations</div>
          {loadingStations ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
          ) : (
            <>
              <table className="data-table" style={{ marginTop: '16px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedStationIds.length === stations.length && stations.length > 0}
                        onChange={handleToggleSelectAll}
                        style={{ cursor: 'pointer', verticalAlign: 'middle', width: '16px', height: '16px' }}
                      />
                    </th>
                    <th>{t('code')}</th>
                    <th>{t('station')}</th>
                    <th style={{ textAlign: 'right' }}>{t('revenue')}</th>
                    <th style={{ textAlign: 'right' }}>{t('share')}</th>
                    <th style={{ textAlign: 'right' }}>{t('transactions')}</th>
                    <th style={{ textAlign: 'center' }}>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.slice((stationPage - 1) * STATIONS_PER_PAGE, stationPage * STATIONS_PER_PAGE).map((s) => (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => onNavigateStation(s.id)}>
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedStationIds.includes(s.id)}
                          onChange={() => handleToggleStationSelection(s.id)}
                          style={{ cursor: 'pointer', verticalAlign: 'middle', width: '16px', height: '16px' }}
                        />
                      </td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text3)' }}>{s.code}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '3px', height: '28px', background: moduleColor, borderRadius: '2px', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '13px' }}>
                              {lang === 'lo' ? s.name_la : s.name_en}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{s.location}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: '600', fontFamily: 'var(--mono)', fontSize: '12px', color: moduleColor }}>
                        {s.amountFormatted}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '600' }}>{s.pct}%</span>
                          <div style={{ width: '80px', height: '4px', background: 'var(--bg4)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${s.pct}%`, background: moduleColor, borderRadius: '2px' }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px' }}>
                        {(s.txn || 0).toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                          background: s.status === 'ok' ? 'rgba(82,196,26,.15)' : 'rgba(250,140,22,.15)',
                          color: s.status === 'ok' ? '#52c41a' : '#fa8c16',
                        }}>
                          {s.status === 'ok' ? '✓ OK' : '⚠ LAG'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {Math.ceil(stations.length / STATIONS_PER_PAGE) > 1 && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text3)', marginRight: '8px' }}>
                    {stationPage} / {Math.ceil(stations.length / STATIONS_PER_PAGE)} ({stations.length} stations)
                  </span>
                  <button onClick={() => setStationPage(p => Math.max(1, p - 1))} disabled={stationPage === 1}
                    style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: stationPage === 1 ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: 'var(--font)', opacity: stationPage === 1 ? 0.4 : 1 }}>
                    {t('prev')}
                  </button>
                  <button onClick={() => setStationPage(p => Math.min(Math.ceil(stations.length / STATIONS_PER_PAGE), p + 1))} disabled={stationPage === Math.ceil(stations.length / STATIONS_PER_PAGE)}
                    style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: stationPage === Math.ceil(stations.length / STATIONS_PER_PAGE) ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: 'var(--font)', opacity: stationPage === Math.ceil(stations.length / STATIONS_PER_PAGE) ? 0.4 : 1 }}>
                    {t('next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Today vs Yesterday percentage comparison from trend
  const todayYesterdayPct = (() => {
    if (dailyTrend.length < 2) return null;
    const latest = dailyTrend[dailyTrend.length - 1].amount;
    const prev = dailyTrend[dailyTrend.length - 2].amount;
    if (prev <= 0) return null;
    const diff = latest - prev;
    const pct = (diff / prev) * 100;
    return {
      pct: Math.abs(pct).toFixed(1),
      up: pct >= 0,
      val: diff >= 0 ? `+${(diff * 1000).toFixed(1)}M` : `-${Math.abs(diff * 1000).toFixed(1)}M`
    };
  })();

  // Calculate daily changes for the last 5 days
  const dailyChanges = (() => {
    if (dailyTrend.length === 0) return [];
    const sliceCount = 5;
    const startIndex = Math.max(0, dailyTrend.length - sliceCount);
    const result = [];
    for (let i = startIndex; i < dailyTrend.length; i++) {
      const current = dailyTrend[i];
      let pct = null;
      let up = true;
      if (i > 0) {
        const prev = dailyTrend[i - 1];
        if (prev.amount > 0) {
          const diffPct = ((current.amount - prev.amount) / prev.amount) * 100;
          pct = Math.abs(diffPct).toFixed(1);
          up = diffPct >= 0;
        }
      }
      const dt = new Date(current.date + 'T00:00:00');
      const formattedDate = dt.toLocaleDateString(lang === 'lo' ? 'lo-LA' : lang === 'th' ? 'th-TH' : 'en-US', { day: '2-digit', month: 'short' });
      result.push({
        date: formattedDate,
        amount: current.amount,
        pct,
        up
      });
    }
    return result.reverse();
  })();

  // Station drill-down detail
  const stationData = stations.find(s => s.id === station.id) || station;

  return (
    <div className="main">
      <button
        onClick={onBack}
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', color: 'var(--text2)', fontSize: '12px', fontFamily: 'var(--font)', marginBottom: '16px' }}
      >
        {t('back')}
      </button>

      <div className="section-header">
        <div>
          <div className="section-title" style={{ color: moduleColor }}>
            {lang === 'lo' ? stationData.name_la : stationData.name_en}
          </div>
          <div className="section-sub" style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>
            {stationData.code} · {stationData.location}
          </div>
        </div>
        <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: stationData.status === 'ok' ? 'rgba(82,196,26,.15)' : 'rgba(250,140,22,.15)', color: stationData.status === 'ok' ? '#52c41a' : '#fa8c16' }}>
          {stationData.status === 'ok' ? '✓ ONLINE' : '⚠ LAGGING'}
        </span>
      </div>

      {/* Station KPIs */}
      <div className="kpi-grid">
        {[
          { 
            label: t('revenue'), 
            val: stationData.amountFormatted || fmtAmount(stationData.amount), 
            color: moduleColor,
            sub: todayYesterdayPct ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', marginTop: '4px' }}>
                <span style={{ color: todayYesterdayPct.up ? '#52c41a' : '#f5222d', fontWeight: 'bold' }}>
                  {todayYesterdayPct.up ? '↑' : '↓'} {todayYesterdayPct.pct}%
                </span>
                <span style={{ color: 'var(--text3)' }}>{t('vsYesterday')}</span>
              </div>
            ) : null
          },
          { label: t('transactions'), val: (stationData.txn || 0).toLocaleString(), color: '#1890ff', sub: <div className="kpi-sub kpi-up">active transactions</div> },
          { label: t('share'), val: `${stationData.pct || 0}%`, color: '#9254de', sub: <div className="kpi-sub kpi-up">of module revenue</div> },
          { label: t('status'), val: stationData.status === 'ok' ? t('ok') : 'Lagging', color: stationData.status === 'ok' ? '#52c41a' : '#fa8c16', sub: <div className="kpi-sub kpi-up">connection state</div> },
        ].map((k, i) => (
          <div key={i} className="kpi">
            <div className="kpi-accent" style={{ background: k.color }} />
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value" style={{ color: k.color }}>{k.val}</div>
            {k.sub}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="chart-row">
        <div className="card">
          <div className="card-title">{t('growthTitle')}</div>
          <div style={{ height: '180px', marginTop: '12px' }}>
            <Bar data={growthChartData} options={growthChartOptions} plugins={[barLabelsPlugin]} />
          </div>
          <div style={{ marginTop: '16px', paddingTop: '10px', borderTop: '1px solid var(--border)', fontSize: '11px', color: 'var(--text3)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontWeight: '600', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🧮</span> {lang === 'lo' ? 'ສູດຄິດໄລ່ອັດຕາການເຕີບໂຕ:' : lang === 'th' ? 'สูตรคำนวณอัตราการเติบโต:' : 'Growth Calculation Formula:'}
            </div>
            <div style={{ fontFamily: 'var(--mono)', background: 'rgba(255,255,255,0.02)', padding: '6px 10px', borderRadius: '4px', border: '1px dashed var(--border)', color: 'var(--text2)', textAlign: 'center', fontSize: '9px', lineHeight: '1.4' }}>
              {t('formula')}
            </div>
          </div>
        </div>
        <div className="card" style={{ flex: 1.3 }}>
          <div className="card-title">{t('dailyTitle')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '16px', alignItems: 'center', marginTop: '12px' }}>
            <div style={{ height: '180px' }}>
              <Line data={dailyChartData} options={miniOptions((v) => `₭${v.toLocaleString()}M`)} />
            </div>
            {/* List of daily comparisons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto', paddingLeft: '8px', borderLeft: '1px solid var(--border)' }}>
              {dailyChanges.map((d, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0', borderBottom: index < dailyChanges.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div>
                    <span style={{ color: 'var(--text2)', fontWeight: '500' }}>{d.date}</span>
                    <div style={{ fontSize: '9px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>₭{(d.amount * 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M</div>
                  </div>
                  {d.pct !== null ? (
                    <span style={{ color: d.up ? '#52c41a' : '#f5222d', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {d.up ? '↑' : '↓'} {d.pct}%
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text3)' }}>-</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title">{t('recentTx')}</div>
          <div style={{ fontSize: '11px', color: 'var(--text3)' }}>
            {txPage * TX_PER_PAGE + 1}–{Math.min((txPage + 1) * TX_PER_PAGE, txList.length)} / {txList.length}
          </div>
        </div>
        <table className="data-table" style={{ marginTop: '12px' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('plate')}</th>
              <th>{t('type')}</th>
              <th>Method</th>
              <th>Payment</th>
              <th>Operator</th>
              <th style={{ textAlign: 'right' }}>{t('amount')}</th>
              <th>{t('time')}</th>
              <th style={{ textAlign: 'center' }}>{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {pagedTx.map((tx) => (
              <tr key={tx.id}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--text3)' }}>{tx.id}</td>
                <td style={{ fontWeight: '500' }}>{tx.plate}</td>
                <td style={{ fontSize: '12px', color: 'var(--text2)' }}>{tx.type}</td>
                <td style={{ fontSize: '12px', color: 'var(--text2)' }}>{tx.method || '-'}</td>
                <td style={{ fontSize: '12px', color: 'var(--text2)' }}>{tx.paymentType || '-'}</td>
                <td style={{ fontSize: '12px', color: 'var(--text2)' }}>{tx.operator || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: '600', fontFamily: 'var(--mono)', color: moduleColor }}>
                  {tx.amount.toLocaleString()}
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text3)' }}>{tx.time}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: statusColors[tx.status]?.bg, color: statusColors[tx.status]?.color }}>
                    {statusColors[tx.status]?.label}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
          <button onClick={() => setTxPage(p => Math.max(0, p - 1))} disabled={txPage === 0}
            style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: txPage === 0 ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: 'var(--font)', opacity: txPage === 0 ? 0.4 : 1 }}>
            {t('prev')}
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
            <button key={i} onClick={() => setTxPage(i)}
              style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border)', background: txPage === i ? moduleColor : 'var(--bg3)', color: txPage === i ? '#fff' : 'var(--text2)', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--font)', fontWeight: txPage === i ? '700' : '400' }}>
              {i + 1}
            </button>
          ))}
          <button onClick={() => setTxPage(p => Math.min(totalPages - 1, p + 1))} disabled={txPage >= totalPages - 1}
            style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: txPage >= totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: 'var(--font)', opacity: txPage >= totalPages - 1 ? 0.4 : 1 }}>
            {t('next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StationDetail;
